import * as React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Minus } from "iconsax-reactjs";
import {
  Calendar,
  ChevronDown,
  X,
} from "lucide-react";
import dayjs from "dayjs";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Shimmer,
} from "../../../shared/ui";
import MultiSelectDropdown from "./MultiSelectDropdown";
import KybComplianceRequestSuccessModal from "./KybComplianceRequestSuccessModal";
import { KybComplianceDetail } from "../types";
import { useMutation } from "@tanstack/react-query";
import { IkybAddProps, kybAdditionalDataApi } from "../../../shared/api/kyb";
import { toast } from "sonner";
import { INFO_TYPES, OptionGeneratorFunc } from "../constants/stats";

export const InfoLoader = () => (
  <div className="space-y-6">
    <div className="space-y-2">
      <Shimmer className="h-4 w-36 rounded-[6px]" />
      <Shimmer className="h-14 w-full rounded-[10px]" />
    </div>
    <div className="space-y-2">
      <Shimmer className="h-4 w-28 rounded-[6px]" />
      <Shimmer className="h-14 w-full rounded-[10px]" />
    </div>
  </div>
)

interface KybComplianceRequestAdditionalInfoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  detail: KybComplianceDetail;
  businessCatalogue: any,
  businessCatalogueLoading: boolean
}

interface OtherField {
  id: string;
  label: string;
  type: string;
}


const KybComplianceRequestAdditionalInfoModal = ({
  open,
  onOpenChange,
  detail,
  businessCatalogue,
  businessCatalogueLoading
}: KybComplianceRequestAdditionalInfoModalProps) => {
  const [selectedInfo, setSelectedInfo] = React.useState<string[]>([]);
  const [otherFields, setOtherFields] = React.useState<OtherField[]>([]);
  const [dueDate, setDueDate] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [showSuccess, setShowSuccess] = React.useState(false);

  const { options, groups, optionMetaMap } = React.useMemo(() => OptionGeneratorFunc({ businessCatalogue, detail, isAddInfo: true }), [businessCatalogue, detail]);

  const { mutateAsync: handleAddKybInfoFunc, isPending } = useMutation({
    mutationFn: (data: IkybAddProps['data']) => kybAdditionalDataApi({ businessId: detail?.id, data }),
    mutationKey: ["add-kyb-information"],
    onSettled: (data, error: any) => {
      if (error) {
        console.error("Error submitting Request Information Update:", error);
        const errorRes = error?.response?.data?.message
        if (typeof errorRes === "string") {
          toast.error(errorRes)
        } else {
          toast.error(errorRes?.[0])
        }
      }
      if (data) {
        setShowSuccess(true);
      }
    }
  })

  const handleSelectedInfoChange = (nextInfo: string[]) => {
    setSelectedInfo(nextInfo);
    const hasOther = nextInfo.includes("other");
    if (hasOther) {
      if (otherFields.length === 0) {
        setOtherFields([{ id: "1", label: "", type: "TEXT" }]);
      }
    } else {
      setOtherFields([]);
    }
  };

  const isOtherSelected = selectedInfo.includes("other");

  const handleOpenChange = (nextOpen: boolean) => {
    if (isSubmitting) return;
    onOpenChange(nextOpen);
    if (!nextOpen) {
      // Reset state
      setSelectedInfo([]);
      setOtherFields([]);
      setDueDate("");
      setShowSuccess(false);
    }
  };

  const handleAddOtherField = () => {
    setOtherFields([
      ...otherFields,
      { id: Math.random().toString(), label: "", type: "TEXT" },
    ]);
  };

  const handleRemoveOtherField = (id: string) => {
    const updated = otherFields.filter((f) => f.id !== id);
    setOtherFields(updated);
    if (updated.length === 0) {
      setSelectedInfo(selectedInfo.filter((val) => val !== "other"));
    }
  };

  const handleOtherFieldChange = (id: string, label: string) => {
    setOtherFields(
      otherFields.map((f) => (f.id === id ? { ...f, label } : f)),
    );
  };

  const handleOtherFieldTypeChange = (id: string, type: string) => {
    setOtherFields(
      otherFields.map((f) => (f.id === id ? { ...f, type } : f)),
    );
  };

  const hasSelection =
    selectedInfo.length > 0 &&
    (!isOtherSelected || otherFields.some((f) => f.label.trim() !== ""));

  const handleSubmit = async () => {
    if (!hasSelection || isSubmitting || isPending) return;
    setIsSubmitting(true);
    try {
      const formattedItems = selectedInfo
        .filter((val) => val !== "other")
        .map((val) => {
          const meta = optionMetaMap.get(val);
          if (meta) {
            const itemObj: {
              key: string;
              subtype?: string;
              targetId?: string;
              targetEmail?: string;
            } = { key: meta.key };

            if (meta.subtype) itemObj.subtype = meta.subtype;
            if (meta.targetId) itemObj.targetId = meta.targetId;
            if (meta.targetEmail) itemObj.targetEmail = meta.targetEmail;

            return itemObj;
          }
          return { key: val };
        });

      const payload: IkybAddProps["data"] = {
        items: [
          ...formattedItems,
          ...otherFields
            .filter((f) => f.label.trim() !== "")
            .map((item) => ({ label: item.label, type: item.type })),
        ],
      };
      if (dueDate) payload.dueDate = dueDate;
      await handleAddKybInfoFunc(payload);
    } catch (error) {
      console.error("Error submitting Request Information Update:", error);
    } finally {
      setIsSubmitting(false);
    }
  };


  const dateInputRef = React.useRef<HTMLInputElement>(null);

  if (showSuccess) {
    return (
      <KybComplianceRequestSuccessModal
        open={open}
        onOpenChange={handleOpenChange}
      />
    );
  }

  const isDisable = !hasSelection || isSubmitting || isPending || businessCatalogueLoading;

  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      overlayClassName="bg-black/75"
      contentClassName="max-w-[600px] bg-white rounded-[24px]"
    >
      <ModalHeader className="items-center justify-between pb-2 border-b border-[#EBEDF9]">
        <ModalTitle className="text-[20px] font-[600] text-[#161827]">
          Request additional information
        </ModalTitle>
        <button
          type="button"
          aria-label="Close modal"
          disabled={isSubmitting}
          onClick={() => handleOpenChange(false)}
          className="rounded-full p-2 text-[#161827] hover:bg-[#F2F4FF] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <X size={20} />
        </button>
      </ModalHeader>

      <ModalBody className="py-6 space-y-[24px]">
        {businessCatalogueLoading ? (
          <InfoLoader />
        ) : (
          <>
              <div className="space-y-1">
                <p className="text-sm font-[450] text-[var(--text-10)]">
                  Required information
                </p>
          <MultiSelectDropdown
            options={options}
            groups={groups}
            selectedValues={selectedInfo}
            onChange={handleSelectedInfoChange}
            label="Required information"
            placeholder="Select information"
            showLabel={false}
          />
        </div>

        {isOtherSelected && otherFields.length > 0 && (
          <div className="">
            {otherFields.length > 1 ? null : <p className="text-sm font-[450] text-[var(--text-10)]">
              Other information
            </p>}
            <div className="space-y-6">
              {otherFields.map((field, idx) => (
                <div
                  key={field.id}
                  className="rounded-[16px] space-y-2 relative"
                >
                  {otherFields.length > 1  ?(
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-[450] text-[var(--text-10)]">
                      Information {idx + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveOtherField(field.id)}
                      className="flex items-center gap-2 text-[13px] font-[450]"
                    >
                     <Minus />  Remove
                    </button>
                  </div>) : null}

                  <div className="space-y-3">
                    <div>
                      <input
                        type="text"
                        value={field.label}
                        onChange={(e) =>
                          handleOtherFieldChange(field.id, e.target.value)
                        }
                        placeholder="E.g. Affidavit"
                        className="h-14 w-full rounded-[8px]  bg-[#E9ECF8]  px-4 text-[14px] text-[#161827] placeholder:text-[#9A9CC0] focus:outline-none focus:border-[#6F00FF]"
                      />
                    </div>

                    <div>
                      <DropdownMenu.Root>
                        <DropdownMenu.Trigger asChild>
                          <button
                            type="button"
                            className="flex h-14 w-full items-center justify-between rounded-[8px] border border-[#E1E6FF] bg-[#E9ECF8] px-4 py-1 text-left focus:outline-none cursor-pointer transition-colors hover:bg-[#E2E6F5]"
                          >
                            <div className="flex flex-col justify-center flex-1 min-w-0 pr-2">
                              <span className="text-sm font-[450] text-[var(--text-30)] select-none">
                                Information type
                              </span>
                              {(() => {
                                const selectedOpt = INFO_TYPES.find(
                                  (opt) =>
                                    opt.value.toLowerCase() === field.type?.toLowerCase() ||
                                    opt.label.toLowerCase() === field.type?.toLowerCase()
                                );
                                if (selectedOpt) {
                                  return (
                                    <div className="flex items-center gap-2 mt-0.5 min-w-0">
                                      <span className="text-[13px] font-[400] text-[#161827] truncate">
                                        {selectedOpt.label}
                                      </span>
                                    </div>
                                  );
                                }
                                return (
                                  <span className="text-[13px] font-[400] text-[#737AA8] truncate mt-0.5">
                                    Select type...
                                  </span>
                                );
                              })()}
                            </div>
                            <ChevronDown size={18} className="text-[#6F739B] shrink-0" />
                          </button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Portal>
                          <DropdownMenu.Content
                            align="start"
                            sideOffset={6}
                            className="z-[125] max-h-[220px] w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto rounded-[12px] border border-[#E1E6FF] bg-white p-1.5 shadow-lg"
                          >
                            {INFO_TYPES.map((item) => {
                              const Icon = item.icon;
                              const isSelected =
                                field.type?.toLowerCase() === item.value.toLowerCase() ||
                                field.type?.toLowerCase() === item.label.toLowerCase();
                              return (
                                <DropdownMenu.Item
                                  key={item.value}
                                  onSelect={() =>
                                    handleOtherFieldTypeChange(field.id, item.value)
                                  }
                                  className={`flex items-center gap-2.5 cursor-pointer rounded-[8px] px-3 py-2 text-[14px] outline-none transition-colors focus:bg-[#F5F6FD] ${isSelected
                                    ? "bg-[#F1ECFF] text-[#5E01D6] font-[500]"
                                    : "text-[#383B56]"
                                    }`}
                                >
                                  <Icon
                                    size={17}
                                    className={isSelected ? "text-[#5E01D6] shrink-0" : "text-[#6F739B] shrink-0"}
                                  />
                                  <span className="truncate">{item.label}</span>
                                </DropdownMenu.Item>
                              );
                            })}
                          </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                      </DropdownMenu.Root>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleAddOtherField}
              className="inline-flex hover:bg-transparent py-0 items-center gap-2 text-base font-[450] text-[var(--primary)] mt-3"
            >
              <span>+</span>  <span>Add</span>
            </Button>
          </div>
        )}

        <div className="space-y-1">
          <p className="text-sm font-[450] text-[var(--text-10)]">
            Due date <span className="font-[400] text-[#7E83AD] italic">• Optional</span>
          </p>
          <div className="relative">
            <input
              ref={dateInputRef}
              type="date"
              className="absolute inset-0 w-full h-[100%] px-4 opacity-0 pointer-events-none z-30"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
            <div
              onClick={() => dateInputRef.current?.showPicker()}
              className="flex h-14 w-full items-center justify-between rounded-[10px] bg-[#E9ECF8] px-5 text-left border border-transparent cursor-pointer"
            >
              <span
                className={`text-[14px] font-[400] ${
                  dueDate ? "text-[#161827]" : "text-[#737AA8]"
                }`}
              >
                {dueDate ? dayjs(dueDate).format("MMM D, YYYY") : "Select date..."}
              </span>
              <Calendar size={20} className="text-[#6F739B]" />
            </div>
          </div>
        </div>
          </>
        )}
      </ModalBody>

      <ModalFooter className="flex items-center justify-end gap-3 pt-4 border-t border-[#EBEDF9] px-6 pb-6">
        <button
          type="button"
          disabled={isSubmitting}
          onClick={() => handleOpenChange(false)}
          className="h-11 px-5 text-[15px] font-[500] text-[#383B56] hover:bg-[#F5F6FD] rounded-[10px] transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <Button
          type="button"
          disabled={isDisable}
          onClick={handleSubmit}
          className="h-11 rounded-[10px] px-6 text-[15px] font-[500] bg-[#161827] text-white hover:bg-[#2B2F49] disabled:bg-[#9A9CC0] disabled:cursor-not-allowed"
        >
          {isSubmitting ? "Sending..." : "Send request"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default KybComplianceRequestAdditionalInfoModal;
